/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

import { useDebounce, useForkRef } from "../../../hooks";

export interface IInputBaseComponentProps
	extends React.HTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
	// Accommodate arbitrary additional props coming from the `inputProps` prop
	[arbitrary: string]: any;
}

export interface IInputBaseProps extends IInputBaseComponentProps {
	className?: string;
	baseClassName?: string;
	prefix?: string;
	suffix?: string;
	autoFocus?: boolean;
	autoFocusSelect?: boolean;
	startAdornment?: React.ReactNode;
	endAdornment?: React.ReactNode;
	readOnly?: boolean;
	debounceTimeout?: number;
	multiLine?: boolean;
	type?: React.HTMLProps<"input">["type"];
	maxRows?: number;
	inputComponent?: React.ElementType<IInputBaseComponentProps>;
	inputRef?: React.Ref<any>;
	preventEdit?: boolean;
}

const InputBase = React.forwardRef<HTMLDivElement, IInputBaseProps>(
	(
		{
			className,
			baseClassName,
			startAdornment,
			endAdornment,
			prefix,
			suffix,
			autoFocus,
			autoFocusSelect,
			value,
			onFocus,
			onBlur,
			readOnly,
			onChange,
			debounceTimeout,
			multiLine,
			type,
			maxRows,
			disabled,
			inputComponent,
			inputRef,
			preventEdit,
			...inputProps
		},
		ref
	) => {
		const inputComponentRef = useRef<HTMLInputElement | HTMLTextAreaElement>();
		const forkRef = useForkRef(inputRef, inputComponentRef);

		const [stateValue, setStateValue] = useState(value);
		const [onChangeDebounced, isDebouncing] = useDebounce(onChange, debounceTimeout);

		const autosizeTextarea = useCallback(() => {
			if (multiLine && inputComponentRef.current instanceof HTMLTextAreaElement) {
				inputComponentRef.current.style.height = "auto";

				if (inputComponentRef.current.scrollHeight > 0) {
					inputComponentRef.current.style.height = `${inputComponentRef.current.scrollHeight}px`;
				}
			}
		}, [multiLine, inputComponentRef]);

		const handleOnChange = useCallback(
			(event) => {
				if (event?.target) {
					const newValue = event.target.value;
					setStateValue(newValue);
				}

				onChangeDebounced(event);
				autosizeTextarea();
			},
			[onChangeDebounced]
		);

		const [focused, setFocused] = useState(
			inputComponentRef.current && inputComponentRef.current === document?.activeElement
		);

		const baseClass = BEM(baseClassName, null, {
			disabled,
			focus: focused,
			"start-adornment": !!startAdornment,
			"end-adornment": !!endAdornment,
			readonly: readOnly,
			multiLine
		});

		const inputValue =
			(prefix || suffix) && !focused ? `${prefix ?? ""}${stateValue ?? ""}${suffix ?? ""}` : stateValue;

		let InputComponent: React.ElementType<IInputBaseComponentProps> = inputComponent;

		if (multiLine && InputComponent === "input") {
			InputComponent = "textarea";
		}

		const inputType: React.HTMLProps<"input">["type"] =
			(prefix || suffix) && type === "number" && !focused ? "text" : type;

		const handleFocus = useCallback(
			(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
				setFocused(true);

				if (isFunction(onFocus)) {
					onFocus(event);
				}

				autosizeTextarea();
			},
			[setFocused, onFocus]
		);

		const handleBlur = useCallback(
			(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
				setFocused(false);

				if (isFunction(onBlur)) {
					onBlur(event);
				}
			},
			[setFocused, onBlur]
		);

		if (className) {
			baseClass.add(className);
		}

		useEffect(() => {
			if (isDebouncing()) {
				return;
			}

			if (value !== stateValue) {
				setStateValue(value);
			}
		}, [value, stateValue, isDebouncing]);

		useEffect(() => {
			if (autoFocus && isFunction(inputComponentRef.current?.focus)) {
				inputComponentRef.current?.focus();

				if (autoFocusSelect && inputComponentRef.current?.setSelectionRange) {
					inputComponentRef.current?.setSelectionRange(0, inputComponentRef.current.value?.length);
				}
			}
		}, [autoFocus, autoFocusSelect]);

		useEffect(() => {
			const element = inputComponentRef.current;
			if (element) {
				const observer = new IntersectionObserver((entries) => {
					if (entries[0].isIntersecting) {
						observer.disconnect();
						autosizeTextarea();
					}
				});
				observer.observe(element);
			}
		}, [inputComponentRef, autosizeTextarea]);

		return (
			<div className={baseClass.toClassName()} ref={ref}>
				{startAdornment}
				<InputComponent
					className={baseClass.element("input").toClassName()}
					ref={forkRef}
					value={inputValue}
					type={inputType}
					onFocus={handleFocus}
					onBlur={handleBlur}
					readOnly={readOnly || preventEdit}
					onChange={handleOnChange}
					rows={maxRows ?? (multiLine ? 1 : undefined)}
					disabled={disabled}
					autoFocus={autoFocus}
					{...inputProps}
				/>
				{endAdornment}
			</div>
		);
	}
);

InputBase.defaultProps = {
	inputComponent: "input",
	baseClassName: "edt-input-base",
	debounceTimeout: 0
};

const InputBaseMemo = React.memo(InputBase);

export { InputBaseMemo as InputBase };
